<template>
  <v-container class="h-full">
    <v-row class="justify-end flex-0">
      <v-col cols="4">
        <v-text-field
          v-model="search"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          class="mb-4"
          clearable
        />
      </v-col>
    </v-row>
    <v-data-table-server
      v-model:items-per-page="itemsPerPage"
      v-model:page="page"
      v-model:sort-by="sortBy"
      :headers="headers"
      :items="items"
      :items-length="itemsLength"
      :items-per-page-options="[10, 25, 50, 100]"
      :loading="loading"
      :search="search"
    >
      <template #item.name="{ item }">
        <p class="pt-2 font-bold text-base" v-text="item.name || ''" />
        <p class="font-light whitespace-nowrap pb-2" v-text="item.id" />
      </template>
      <template #item.client="{ item }">
        <p v-text="item.client || '(no client)'" />
      </template>
      <template #item.slug="{ item }">
        <p class="font-mono" v-text="item.slug || '(no slug)'" />
      </template>
      <template #item.legacyIds="{ item }">
        <p v-text="formatLegacyIds(item)" />
      </template>
      <template #item.actions="{ item }">
        <div class="d-flex justify-center">
          <v-btn
            variant="flat"
            size="32"
            color="primary"
            icon="mdi-pencil"
            @click="viewProgram(item)"
          />
        </div>
      </template>
    </v-data-table-server>
  </v-container>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineComponent, markRaw, onMounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import { useProgramStore } from '@/stores/program'

import { RouteNames } from '@/router'

import type { Program } from '@policyfly/protobuf/pfbingo'
import type { DataTableHeader } from 'vuetify'
import type { SortItem } from 'vuetify/lib/components/VDataTable/composables/sort.mjs'

export default defineComponent({
  name: 'ProgramList',

  setup () {
    const router = useRouter()
    const programStore = useProgramStore()
    const { loading, programs } = storeToRefs(programStore)

    onMounted(async () => {
      await programStore.loadPrograms()
    })

    const headers: DataTableHeader<Program>[] = markRaw([
      { title: 'Name', key: 'name', value: 'name', sortable: true },
      { title: 'Client', key: 'client', value: 'client', sortable: true },
      { title: 'Slug', key: 'slug', value: 'slug', sortable: true },
      { title: 'Legacy IDs', key: 'legacyIds', value: 'legacyIds', sortable: false },
      { title: 'Actions', key: 'actions', value: 'actions', sortable: false, align: 'center' },
    ])
    const search = ref('')

    watch(search, () => {
      page.value = 1
    })

    const itemsPerPage = ref(100)
    const page = ref(1)
    const sortBy = ref<SortItem[]>([])
    const filteredItems = computed(() => {
      if (!search.value) return programs.value
      return programs.value.filter((program) => {
        return Object.values(program).some((val) =>
          String(val).toLowerCase().includes(search.value.toLowerCase()),
        )
      })
    })
    const itemsLength = computed(() => filteredItems.value.length)
    const sortedItems = computed(() => {
      return filteredItems.value.slice().sort((a, b) => {
        for (const sortItem of sortBy.value) {
          const aValue = String(a[sortItem.key as keyof typeof a])
          const bValue = String(b[sortItem.key as keyof typeof b])
          if (aValue < bValue) return sortItem.order === 'desc' ? 1 : -1
          if (aValue > bValue) return sortItem.order === 'desc' ? -1 : 1
        }
        return 0
      })
    })
    const items = computed(() => {
      const offset = (page.value - 1) * itemsPerPage.value
      return sortedItems.value.slice(offset, offset + itemsPerPage.value)
    })

    function formatLegacyIds (program: Program): string {
      const legacyIds = Object.entries(program.legacyIds)
        .map(([environmentId, legacyId]) => `${environmentId}: ${legacyId}`)
        .join(', ')
      return legacyIds || '-'
    }

    function viewProgram (program: Program): void {
      router.push({ name: RouteNames.PROGRAM_EDIT, params: { id: program.id } })
    }

    return {
      loading,
      headers,
      search,
      itemsPerPage,
      page,
      sortBy,
      itemsLength,
      items,
      formatLegacyIds,
      viewProgram,
    }
  },
})
</script>
